import { FC, memo, useContext } from 'react'
import { ThemeContext } from 'styled-components'
import { useTranslation } from '../../../hooks/locale'
import { useCardData } from '../../../hooks/use-card-data'
import { ListingSectionObject } from '../../../transports/common/listing-section-transport'
import { CARD_IMAGE_WIDTH } from '../../../common/image-widths'
import {
    WishlistIconContainer,
    FavoriteButton,
    ObjectTopRecommendationPill,
    TopRecommendationWrapper,
    PartnerLogoContainer,
    PartnerLogo,
    CheckBox,
    CheckBoxLabel,
    InputCheckbox,
    InformationWrapper,
    LocationContainer,
    TraumTagIconStyled,
    PopularChoiceTagContainer,
    BottomTraumTagImage,
    BottomTraumTagText,
    OfferWrapper,
    OfferIcon,
    FullStarIcon,
    QuickResponderTag,
    StyledFastResponderIcon,
    DTVStarContainer,
    DTVRatingText,
    DTVImage,
    PropertyTitleLink,
    BaseNextImageContainer as NextImageContainer,
    BaseDTVLabel as DTVLabel,
    BaseLocationText as LocationText,
    BasePopularChoiceTagText as PopularChoiceTagText,
    BaseSeoHref as SeoHref,
    BaseFreeCancellationIcon as FreeCancellationIcon,
} from '../card-shared-styles'
import {
    BottomRow,
    ButtonWrapper,
    CTAButton,
    PriceWrapper,
    GridCardContainer,
    ImageContainer,
    CardContent,
    CardTopSection,
    SelectAnchor,
    CardBottomContainer,
    TopTraumTagPill,
    RatingText,
    RatingCount,
    PropertyTitle,
    PropertyDescription,
    PriceContainer,
    SlasherPrice,
    LocationRatingContainer,
    PersonDetails,
    RatingBadge,
    OfferBadge,
    OfferText,
    FeatureIconsContainer,
    FreeCancellationContainer,
    FreeCancellationText,
    TraumTagLabel,
    BottomTraumTagContainer,
    BookingHighlights,
    CtaPriceContainer,
    BottomContainerWrapper,
    DTVContainer,
    TooltipContainer,
    VirtualTourWrapper,
} from './styles'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'
import { Offer } from '../../../models/common/offers-model'
import { SuitablesModel } from '../../../models/common/suitables-model'
import get from 'lodash/get'

import NextImage from '../next-image'
import SelectCheckbox from '../select-checkbox'
import FormattedPrice from '../formatted-price'
import InlineFeatures from '../features/features'
import Star from '../../../assets/icons/star.svg'
import WishlistSection from '../wishlist-section'
import { preventDefaultHandler } from '../../utils'
import { stopPropagation } from '../../../utils/dom-utils'
import ImageCarousel from '../../common/image-carousel'
import { getBasePath, getPartnerLogoUrl } from '../../../utils/miscellaneous-utils'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'
import VirtualTourTag from '../virtual-tour-tag'
import { VirtualTourPosition } from '../../property-details/constants'
import { pageTypeEventCategories } from '../../../services/analytics-service/categories'

interface GridCardProps {
    index: number
    listingObject: ListingSectionObject
    requestUrl?: string
    displayPrice?: boolean
    displayPriceInfo?: boolean
    showCtaButton?: boolean
    isObjectOverview?: boolean
    pageName: string
    sectionName: string
    showMaxPersonCount?: boolean
    showCheckbox?: boolean
    isSelected?: boolean
    isCheckboxDisabled?: boolean
    disabledTooltip?: string
    checkboxVariant?: 'overlay' | 'inline'
    onToggleSelect?: (id: number) => void
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
    useCarousel?: boolean
    showFeatures?: boolean
    showFeatureLabel?: boolean
    isAboveFold?: boolean
    distanceFromParentLocation?: string
    propagateSearchRequestId?: boolean
}

const GridCard: FC<GridCardProps> = ({
    index,
    listingObject,
    requestUrl,
    displayPrice = true,
    displayPriceInfo = true,
    showCtaButton = false,
    isObjectOverview = false,
    pageName,
    sectionName,
    showMaxPersonCount = false,
    showCheckbox = false,
    isSelected = false,
    isCheckboxDisabled = false,
    disabledTooltip,
    checkboxVariant = 'overlay',
    onToggleSelect,
    breadcrumbModel,
    offerModel,
    suitablesModel,
    useCarousel = false,
    showFeatures = true,
    showFeatureLabel = true,
    isAboveFold = false,
    distanceFromParentLocation,
    propagateSearchRequestId = false,
}) => {
    const theme = useContext(ThemeContext)!
    const { t } = useTranslation()

    const cardData = useCardData({
        index,
        listingObject,
        requestUrl,
        pageName,
        sectionName,
        displayPrice,
        displayPriceInfo,
        showCheckbox,
        showMaxPersonCount,
        isSelected,
        onToggleSelect,
        isObjectOverview,
        propagateSearchRequestId,
        breadcrumbModel,
        offerModel,
        suitablesModel,
    })

    const {
        listingId,
        propertyTitle,
        propertyDescriptionText,
        regionName,
        regionLink,
        propertyImage,
        priceValue,
        priceInBaseUnits,
        priceBeforeOffer,
        priceCurrency,
        priceInfoValue,
        formattedOffer,
        isFreeCancellation,
        shouldShowRating,
        formattedRatingAverage,
        formattedRatingCount,
        hasFeatures,
        computedFeatures,
        traumTagsData,
        currentBadgeTraumTag,
        isTopObject,
        isPremiumTopObject,
        virtualTourVideo,
        intersectionTargetRef,
        handleCardClick,
        handleCheckboxChange,
        handleCtaClick,
        href,
        seoHref,
        noCrawl,
        showSuperfastResponderTag,
        isDirectBooking,
        cardImages,
        partnerName,
        classification,
    } = cardData

    const { isProvidedVariantActive: isSlasherPriceActive } = useABVariant(AB_KEY.SLASHER_PRICE, AB_VARIANT_VERSION.ONE)

    if (!listingObject) {
        return null
    }

    const dtvlogoPath = getBasePath('/assets/images/dtv-logo.png')

    return (
        <GridCardContainer ref={intersectionTargetRef} onClick={handleCardClick} $isObjectOverview={isObjectOverview}>
            <ImageContainer>
                {showCheckbox && checkboxVariant === 'overlay' && (
                    <InformationWrapper onClick={stopPropagation}>
                        <CheckBox>
                            <InputCheckbox
                                type="checkbox"
                                checked={isSelected}
                                onChange={handleCheckboxChange}
                                id={`checkbox-${listingId}`}
                            />
                            <CheckBoxLabel htmlFor={`checkbox-${listingId}`} />
                        </CheckBox>
                    </InformationWrapper>
                )}
                {useCarousel ? (
                    <ImageCarousel
                        images={cardImages}
                        aspectRatio="66.66%"
                        imageCarouselBorderRadius="1rem 1rem 0 0"
                        imageCarouselMargin="0"
                        isAboveFold={isAboveFold}
                        imwidth={partnerName ? undefined : CARD_IMAGE_WIDTH}
                    />
                ) : (
                    !!propertyImage && (
                        <NextImage
                            containerComponent={NextImageContainer}
                            src={get(propertyImage, 'url', '')}
                            alt={get(propertyImage, 'alt', 'Property image')}
                            imwidth={partnerName ? undefined : CARD_IMAGE_WIDTH}
                            sizes="(max-width: 767px) 100vw, 360px"
                        />
                    )
                )}
                {!!virtualTourVideo && (
                    <VirtualTourWrapper shouldShowRating={shouldShowRating}>
                        <VirtualTourTag
                            analyticsCategory={pageTypeEventCategories.SEARCH_RESULT_LISTING_PAGE}
                            analyticsLabel={VirtualTourPosition.GRID_CARD}
                            video={virtualTourVideo}
                            handleCtaClick={handleCtaClick}
                            isSmallVersion
                        />
                    </VirtualTourWrapper>
                )}
                {traumTagsData.badgeTraumTags.length > 0 && currentBadgeTraumTag && currentBadgeTraumTag.Icon && (
                    <TopTraumTagPill key={currentBadgeTraumTag.id} $showCheckbox={showCheckbox}>
                        <TraumTagIconStyled as={currentBadgeTraumTag.Icon} />
                        <TraumTagLabel>{t(currentBadgeTraumTag.label)}</TraumTagLabel>
                    </TopTraumTagPill>
                )}
                {(isTopObject || isPremiumTopObject) && (
                    <ObjectTopRecommendationPill>
                        <TopRecommendationWrapper>{t('topListing')}</TopRecommendationWrapper>
                    </ObjectTopRecommendationPill>
                )}
                {partnerName ? (
                    <PartnerLogoContainer $partnerName={partnerName}>
                        <PartnerLogo
                            src={getPartnerLogoUrl(partnerName)}
                            alt={`${partnerName} logo`}
                            $partnerName={partnerName}
                        />
                    </PartnerLogoContainer>
                ) : (
                    <FavoriteButton onClick={stopPropagation}>
                        <WishlistIconContainer>
                            <WishlistSection
                                containerBorderRadius="1.456rem"
                                containerHeight="1.5rem"
                                containerWidth="1.5rem"
                                containerMargin="0"
                                containerPadding="0"
                                width="1.5rem"
                                height="1.5rem"
                                listingId={listingId?.toString() || ''}
                                showBoxShadow={false}
                                strokeColor="white"
                                containerBackgroundColor="transparent"
                            />
                        </WishlistIconContainer>
                    </FavoriteButton>
                )}

                {shouldShowRating && (
                    <RatingBadge>
                        <FullStarIcon as={Star} />
                        <RatingText>
                            {formattedRatingAverage} <RatingCount>{formattedRatingCount}</RatingCount>
                        </RatingText>
                    </RatingBadge>
                )}
            </ImageContainer>

            <CardContent $hasBottomTags={traumTagsData.hasBottomTags}>
                {showCheckbox && checkboxVariant === 'inline' && (
                    <SelectAnchor>
                        <SelectCheckbox
                            id={`checkbox-${listingId}`}
                            checked={isSelected}
                            disabled={isCheckboxDisabled}
                            disabledTooltip={disabledTooltip}
                            label={t('select')}
                            layout="stacked"
                            onChange={handleCheckboxChange}
                            onClick={stopPropagation}
                        />
                    </SelectAnchor>
                )}
                <CardTopSection>
                    <LocationRatingContainer>
                        <LocationContainer>
                            <SeoHref title={regionName} href={regionLink ?? undefined} onClick={stopPropagation}>
                                <LocationText>{regionName}</LocationText>
                            </SeoHref>
                            {!!distanceFromParentLocation && (
                                <LocationText> | {distanceFromParentLocation}</LocationText>
                            )}
                        </LocationContainer>
                        {traumTagsData.popularChoiceTag && (
                            <PopularChoiceTagContainer>
                                {traumTagsData.popularChoiceTag.Icon && (
                                    <TraumTagIconStyled as={traumTagsData.popularChoiceTag.Icon} />
                                )}
                                <PopularChoiceTagText>{t(traumTagsData.popularChoiceTag.label)}</PopularChoiceTagText>
                            </PopularChoiceTagContainer>
                        )}
                    </LocationRatingContainer>
                    <PropertyTitle $reserveSelectSpace={showCheckbox && checkboxVariant === 'inline'}>
                        <PropertyTitleLink
                            autoRedirect={false}
                            title={propertyTitle}
                            href={seoHref ?? href}
                            onClick={preventDefaultHandler}
                            noCrawl={noCrawl ?? false}
                        >
                            {propertyTitle}
                        </PropertyTitleLink>
                    </PropertyTitle>

                    {propertyDescriptionText && (
                        <PropertyDescription $reserveSelectSpace={showCheckbox && checkboxVariant === 'inline'}>
                            {propertyDescriptionText}
                        </PropertyDescription>
                    )}

                    {hasFeatures && showFeatures && (
                        <FeatureIconsContainer>
                            <InlineFeatures
                                features={computedFeatures}
                                color={theme.colors.tfwBlack99}
                                size={1.2}
                                gap={0.5}
                                visibleFeatures={5}
                                showLabel={showFeatureLabel}
                            />
                        </FeatureIconsContainer>
                    )}
                </CardTopSection>

                {(showSuperfastResponderTag || isDirectBooking || classification) && (
                    <BookingHighlights>
                        {classification && (
                            <DTVContainer>
                                <DTVStarContainer>
                                    <FullStarIcon as={Star} />
                                    <DTVRatingText>{get(classification, 'starsCount')} / 5</DTVRatingText>
                                </DTVStarContainer>
                                <DTVImage src={dtvlogoPath} aria-label="DTV Logo" />
                                <DTVLabel>{t('classification.default.title')}</DTVLabel>
                                {classification.description && (
                                    <TooltipContainer>{classification.description}</TooltipContainer>
                                )}
                            </DTVContainer>
                        )}
                        {isDirectBooking && (
                            <QuickResponderTag>
                                <StyledFastResponderIcon /> {t('directBookingTagLabel')}
                            </QuickResponderTag>
                        )}
                        {showSuperfastResponderTag && (
                            <QuickResponderTag>
                                <StyledFastResponderIcon /> {t('quickResponderText')}
                            </QuickResponderTag>
                        )}
                    </BookingHighlights>
                )}

                <CardBottomContainer>
                    <BottomContainerWrapper>
                        {formattedOffer && (
                            <OfferBadge>
                                <OfferIcon />
                                <OfferText>
                                    {get(formattedOffer, 'discount', '')} · {get(formattedOffer, 'label', '')}
                                </OfferText>
                            </OfferBadge>
                        )}
                        {isFreeCancellation && (
                            <FreeCancellationContainer>
                                <FreeCancellationIcon />
                                <FreeCancellationText>{t('freeCancellation')}</FreeCancellationText>
                            </FreeCancellationContainer>
                        )}
                        <CtaPriceContainer>
                            <BottomRow>
                                <PriceWrapper>
                                    {displayPrice && priceValue && (
                                        <PriceContainer>
                                            {isSlasherPriceActive && priceBeforeOffer && (
                                                <SlasherPrice>{priceBeforeOffer}</SlasherPrice>
                                            )}
                                            <FormattedPrice
                                                priceInBaseUnits={priceInBaseUnits}
                                                currency={priceCurrency}
                                                fallbackPrice={priceValue}
                                            />
                                        </PriceContainer>
                                    )}
                                    {displayPriceInfo && priceInfoValue && (
                                        <PersonDetails>{priceInfoValue}</PersonDetails>
                                    )}
                                </PriceWrapper>

                                <OfferWrapper>
                                    {traumTagsData.bottomTraumTags.map(tag => (
                                        <BottomTraumTagContainer key={tag.id}>
                                            {tag.imageUrl && (
                                                <BottomTraumTagImage src={tag.imageUrl} alt={t(tag.label)} />
                                            )}
                                            <BottomTraumTagText>{t(tag.label)}</BottomTraumTagText>
                                        </BottomTraumTagContainer>
                                    ))}
                                </OfferWrapper>
                            </BottomRow>

                            {showCtaButton && (
                                <ButtonWrapper>
                                    <CTAButton onClick={handleCtaClick}>{t('see_offer')}</CTAButton>
                                </ButtonWrapper>
                            )}
                        </CtaPriceContainer>
                    </BottomContainerWrapper>
                </CardBottomContainer>
            </CardContent>
        </GridCardContainer>
    )
}

export default memo(GridCard)
